import { ROUTES } from "@/shared/routing/routes"
import { SyncBackBody, SyncBackPet } from "@/shared/types/lead-api-types"

export type RouteKey =
  | "prepackages"
  | "ownerInformations"
  | "petBirthdate"
  | "liabilityAddOn"
  | "medicalHistory"

export type RouteResolverContext = {
  quote: Partial<SyncBackBody>
  termsAccepted: boolean
  isFromLandingPage: boolean
  currentPetIdx?: number | null
  isEditingSinglePet?: boolean
}

export type RouteResolverResult = {
  route: string
  nextPetIndex?: number | null
}

const hasSelectedPackage = (pet: Partial<SyncBackPet>) => Boolean(pet.pricing?.options?.pack)

export const isMultiPet = (quote?: Partial<SyncBackBody>) => (quote?.pets?.length || 0) > 1

export const getNextPetNeedingPackage = (pets?: Partial<SyncBackPet>[]): number | null => {
  if (!pets || pets.length === 0) {
    return null
  }

  const sortedPets = [...pets]
    .filter((pet): pet is Partial<SyncBackPet> & { idx: number } => typeof pet.idx === "number")
    .sort((a, b) => a.idx - b.idx)

  const targetPet = sortedPets.find((pet) => !hasSelectedPackage(pet))

  return targetPet?.idx ?? null
}

/** The next pet after `idx` in idx order (or undefined when `idx` is the last pet). */
const getNextPetByIdx = (
  idx: number,
  pets?: Partial<SyncBackPet>[]
): (Partial<SyncBackPet> & { idx: number }) | undefined =>
  [...(pets || [])]
    .filter(
      (pet): pet is Partial<SyncBackPet> & { idx: number } =>
        typeof pet.idx === "number" && pet.idx > idx
    )
    .sort((a, b) => a.idx - b.idx)[0]

export const shouldReturnToTribeQuoteAfterBirthdate = (context: RouteResolverContext) =>
  Boolean(context.termsAccepted) ||
  Boolean(context.isFromLandingPage) ||
  // Conversion phase: at least one existing pet already has a formula,
  // so adding a new pet should return to the tribe-quote hub, not /tribe.
  Boolean(context.quote?.pets?.some(hasSelectedPackage))

export const getNextRoute = (
  currentRoute: RouteKey,
  context: RouteResolverContext
): RouteResolverResult => {
  switch (currentRoute) {
    case "prepackages": {
      // Per-pet walk (stable across back-nav): each pet gets its prepackage, then
      // — dogs only — a /liability-add-on RC step, then its /medical-history
      // health declaration, before the next pet. Path for dog0 + cat1:
      // prepackages(0) → liability-add-on(0) → medical-history(0) →
      // prepackages(1) → medical-history(1) → /tribe-quote.
      const currentIdx = context.currentPetIdx
      if (typeof currentIdx === "number") {
        const currentPet = (context.quote?.pets || []).find((pet) => pet.idx === currentIdx)
        // Dog → show its RC overview next, keeping currentPetIdx on this dog.
        // (Health history comes right after, from the liability step.)
        if (currentPet?.type === "dog") {
          return { route: ROUTES.liabilityAddOn.path }
        }
        // Editing a single pet's formula from /tribe-quote → go straight back to
        // the hub instead of continuing the per-pet walk (skips medical-history).
        if (context.isEditingSinglePet) {
          return { route: ROUTES.tribeQuote.path }
        }
        // Cat → its health-history declaration (same pet).
        return { route: ROUTES.medicalHistory.path }
      }
      const nextPetIndex = getNextPetNeedingPackage(context.quote?.pets)
      if (nextPetIndex !== null) {
        return { route: ROUTES.prepackages.path, nextPetIndex }
      }
      return { route: ROUTES.tribeQuote.path }
    }
    case "liabilityAddOn": {
      // Editing a single dog's formula from /tribe-quote → return to the hub
      // once its liability step is done (skips medical-history).
      if (context.isEditingSinglePet) {
        return { route: ROUTES.tribeQuote.path }
      }
      // After a dog's RC overview → that same dog's health-history declaration.
      return { route: ROUTES.medicalHistory.path }
    }
    case "medicalHistory": {
      // Editing a single pet from /tribe-quote bypasses the walk and returns to
      // the hub directly.
      if (context.isEditingSinglePet) {
        return { route: ROUTES.tribeQuote.path }
      }
      // Continue the per-pet walk to the next pet's prepackages, or finish at the
      // tribe-quote hub once every pet is done.
      const currentIdx = context.currentPetIdx
      if (typeof currentIdx === "number") {
        const nextPet = getNextPetByIdx(currentIdx, context.quote?.pets)
        if (nextPet) {
          return { route: ROUTES.prepackages.path, nextPetIndex: nextPet.idx }
        }
      }
      return { route: ROUTES.tribeQuote.path }
    }
    case "ownerInformations":
      return { route: ROUTES.recap.path }
    case "petBirthdate":
      return {
        route: shouldReturnToTribeQuoteAfterBirthdate(context) ? ROUTES.tribeQuote.path : ROUTES.tribe.path,
      }
  }
}
